(() => {
  const selectors = {
    stickyHeader: '[data-sticky-header], .site-header[data-sticky="true"]',
    cartDrawer: '[data-cart-drawer]',
    cartOpen: '[data-cart-drawer-open], [href="/cart"][data-drawer-trigger]',
    cartClose: '[data-cart-drawer-close]',
    animate: '[data-animate]',
    disclosure: '[data-disclosure]',
    disclosureToggle: '[data-disclosure-toggle]',
    heroSlider: '[data-hero-slider]',
    productSlider: '[data-product-slider]',
    trustSlider: '[data-trust-slider]',
    beforeAfter: '[data-before-after]'
  };

  const trapStack = [];

  const getFocusable = (container) => {
    if (!container) return [];
    return [...container.querySelectorAll('a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), details, [tabindex]:not([tabindex="-1"])')]
      .filter((element) => !element.hasAttribute('disabled') && element.offsetParent !== null);
  };

  const lockOwners = new Set();
  const setDrawerLock = (locked, owner = 'cart') => {
    if (locked) lockOwners.add(owner);
    else lockOwners.delete(owner);
    const shouldLock = lockOwners.size > 0;
    if (shouldLock && !document.body.classList.contains('drawer-open')) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.setProperty('--scrollbar-compensation', `${scrollbarWidth}px`);
      document.body.style.paddingInlineEnd = `${scrollbarWidth}px`;
    }
    document.body.classList.toggle('drawer-open', shouldLock);
    document.documentElement.classList.toggle('drawer-open', shouldLock);
    if (!shouldLock) {
      document.body.style.paddingInlineEnd = '';
      document.documentElement.style.removeProperty('--scrollbar-compensation');
    }
  };
  window.RosemaryScrollLock = {
    lock(owner = 'header') { setDrawerLock(true, owner); },
    unlock(owner = 'header') { setDrawerLock(false, owner); }
  };
  const syncDrawerLock = () => {
    const hasOpenDrawer = Boolean(document.querySelector(`${selectors.cartDrawer}[aria-hidden="false"]`));
    setDrawerLock(hasOpenDrawer, 'cart');
  };
  const lockBody = () => setDrawerLock(true, 'cart');
  const unlockBody = () => setDrawerLock(false, 'cart');

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('.cart-drawer__panel');
      this.closeButtons = this.querySelectorAll(selectors.cartClose);
      this.previouslyFocused = null;
      this.boundKeydown = this.onKeydown.bind(this);
      this.boundDocumentClick = this.onDocumentClick.bind(this);

      this.closeButtons.forEach((button) => {
        button.addEventListener('click', () => this.close());
      });

      document.addEventListener('click', this.boundDocumentClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.boundDocumentClick);
      document.removeEventListener('keydown', this.boundKeydown);
      const stackIndex = trapStack.indexOf(this);
      if (stackIndex > -1) trapStack.splice(stackIndex, 1);
      requestAnimationFrame(syncDrawerLock);
    }

    onDocumentClick(event) {
      const trigger = event.target.closest(selectors.cartOpen);
      if (!trigger || !window.theme?.settings?.enableCartDrawer) return;
      event.preventDefault();
      this.open(trigger);
    }

    open(trigger = document.activeElement) {
      document.querySelectorAll(selectors.cartDrawer).forEach((drawer) => {
        if (drawer === this) return;
        if (typeof drawer.close === 'function') {
          drawer.close({ restoreFocus: false });
        } else {
          drawer.setAttribute('aria-hidden', 'true');
        }
      });
      this.previouslyFocused = trigger;
      this.setAttribute('aria-hidden', 'false');
      lockBody();
      if (!trapStack.includes(this)) trapStack.push(this);
      document.addEventListener('keydown', this.boundKeydown);
      requestAnimationFrame(() => {
        const firstFocusable = getFocusable(this.panel)[0];
        (firstFocusable || this.panel).focus();
      });
      this.dispatchEvent(new CustomEvent('cart-drawer:open', { bubbles: true }));
    }

    close({ restoreFocus = true } = {}) {
      this.setAttribute('aria-hidden', 'true');
      document.removeEventListener('keydown', this.boundKeydown);
      const stackIndex = trapStack.indexOf(this);
      if (stackIndex > -1) trapStack.splice(stackIndex, 1);
      syncDrawerLock();
      if (restoreFocus && this.previouslyFocused?.isConnected && typeof this.previouslyFocused.focus === 'function') {
        this.previouslyFocused.focus();
      }
      this.dispatchEvent(new CustomEvent('cart-drawer:close', { bubbles: true }));
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = getFocusable(this.panel);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  class Disclosure extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector(selectors.disclosureToggle);
      if (!this.toggle) return;

      this.toggle.addEventListener('click', () => {
        const expanded = this.toggle.getAttribute('aria-expanded') === 'true';
        this.toggle.setAttribute('aria-expanded', String(!expanded));
        this.toggle.closest(selectors.disclosure)?.toggleAttribute('open', !expanded);
      });
    }
  }
  const initLanguageSwitcher = () => {
    document.documentElement.dir = document.documentElement.dir || (document.documentElement.lang?.split('-')[0] === 'ar' ? 'rtl' : 'ltr');
  };

  window.addEventListener('pageshow', () => unlockBody());

  const initStickyHeader = () => {
    if (document.querySelector('[data-rosemary-header]')) return;
    const header = document.querySelector(selectors.stickyHeader);
    if (!header || !window.theme?.settings?.enableStickyHeader) return;
    if (header.dataset.stickyBound === 'true') return;
    header.dataset.stickyBound = 'true';

    let lastY = window.scrollY;
    let ticking = false;

    const update = () => {
      const currentY = window.scrollY;
      header.classList.toggle('is-scrolled', currentY > 12);
      header.classList.toggle('is-hidden', currentY > lastY && currentY > 180);
      lastY = Math.max(currentY, 0);
      ticking = false;
    };

    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });
  };

  const initHeroSliders = () => {
    document.querySelectorAll(selectors.heroSlider).forEach((slider) => {
      if (slider.dataset.sliderReady === 'true') return;
      slider.dataset.sliderReady = 'true';

      const viewport = slider.querySelector('[data-hero-viewport]');
      const slides = [...slider.querySelectorAll('[data-hero-slide]')];
      const dots = [...slider.querySelectorAll('[data-hero-dot]')];
      const prev = slider.querySelector('[data-hero-prev]');
      const next = slider.querySelector('[data-hero-next]');
      const enabled = slider.dataset.sliderEnabled !== 'false';
      const autoplay = slider.dataset.autoplay === 'true';
      const infinite = slider.dataset.infiniteLoop !== 'false';
      const pauseOnHover = slider.dataset.pauseOnHover !== 'false';
      const effect = slider.dataset.effect || 'slide';
      const speed = Number(slider.dataset.autoplaySpeed || 5000);
      let index = 0;
      let timer = null;

      if (!viewport || slides.length === 0) return;

      const normalizeIndex = (nextIndex) => {
        if (infinite) return (nextIndex + slides.length) % slides.length;
        return Math.max(0, Math.min(nextIndex, slides.length - 1));
      };

      const setActive = (nextIndex, shouldScroll = true) => {
        index = normalizeIndex(nextIndex);
        slides.forEach((slide, slideIndex) => {
          slide.classList.toggle('is-active', slideIndex === index);
          slide.setAttribute('aria-hidden', slideIndex === index ? 'false' : 'true');
        });
        dots.forEach((dot, dotIndex) => {
          dot.classList.toggle('is-active', dotIndex === index);
          dot.setAttribute('aria-selected', dotIndex === index ? 'true' : 'false');
        });
        if (shouldScroll && effect !== 'fade') {
          viewport.scrollTo({ left: slides[index].offsetLeft, behavior: 'smooth' });
        }
      };

      const stop = () => {
        if (timer) window.clearInterval(timer);
        timer = null;
      };

      const start = () => {
        stop();
        if (!enabled || slides.length <= 1 || !autoplay || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        timer = window.setInterval(() => {
          if (!infinite && index >= slides.length - 1) {
            stop();
            return;
          }
          setActive(index + 1);
        }, Math.max(speed, 2000));
      };

      if (!enabled || slides.length <= 1) {
        setActive(0, false);
        return;
      }

      prev?.addEventListener('click', () => {
        stop();
        setActive(index - 1);
        start();
      });

      next?.addEventListener('click', () => {
        stop();
        setActive(index + 1);
        start();
      });

      dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          stop();
          setActive(Number(dot.dataset.index || 0));
          start();
        });
      });

      const mobileSwipeQuery = window.matchMedia('(max-width: 768px)');
      let touchStartX = 0;
      let touchStartY = 0;
      let suppressNextClick = false;

      viewport.addEventListener('touchstart', (event) => {
        if (!mobileSwipeQuery.matches || event.touches.length !== 1) return;
        touchStartX = event.touches[0].clientX;
        touchStartY = event.touches[0].clientY;
      }, { passive: true });

      viewport.addEventListener('touchend', (event) => {
        if (!mobileSwipeQuery.matches || event.changedTouches.length !== 1) return;
        const deltaX = event.changedTouches[0].clientX - touchStartX;
        const deltaY = event.changedTouches[0].clientY - touchStartY;
        const horizontalDistance = Math.abs(deltaX);
        const verticalDistance = Math.abs(deltaY);
        touchStartX = 0;
        touchStartY = 0;

        if (horizontalDistance < 50 || horizontalDistance <= verticalDistance * 1.25) return;
        suppressNextClick = true;
        stop();
        setActive(index + (deltaX < 0 ? 1 : -1));
        start();
        window.setTimeout(() => {
          suppressNextClick = false;
        }, 400);
      }, { passive: true });

      viewport.addEventListener('touchcancel', () => {
        touchStartX = 0;
        touchStartY = 0;
      }, { passive: true });

      viewport.addEventListener('click', (event) => {
        if (!suppressNextClick) return;
        event.preventDefault();
        event.stopPropagation();
        suppressNextClick = false;
      }, true);

      let scrollFrame = null;
      if (effect !== 'fade') {
        viewport.addEventListener('scroll', () => {
          if (scrollFrame) window.cancelAnimationFrame(scrollFrame);
          scrollFrame = window.requestAnimationFrame(() => {
            const nearest = slides.reduce((closest, slide, slideIndex) => {
              const distance = Math.abs(slide.offsetLeft - viewport.scrollLeft);
              return distance < closest.distance ? { index: slideIndex, distance } : closest;
            }, { index, distance: Infinity });
            setActive(nearest.index, false);
          });
        }, { passive: true });
      }

      if (pauseOnHover) {
        slider.addEventListener('mouseenter', stop);
        slider.addEventListener('mouseleave', start);
        slider.addEventListener('focusin', stop);
        slider.addEventListener('focusout', start);
        slider.addEventListener('touchstart', stop, { passive: true });
        slider.addEventListener('touchend', start, { passive: true });
      }

      setActive(0, false);
      start();
    });
  };

  const initProductSliders = () => {
    document.querySelectorAll(selectors.productSlider).forEach((slider) => {
      if (slider.dataset.productSliderReady === 'true') return;
      slider.dataset.productSliderReady = 'true';

      const rail = slider.querySelector('[data-slider-rail]');
      const prev = slider.querySelector('[data-slider-prev]');
      const next = slider.querySelector('[data-slider-next]');
      const autoplay = slider.dataset.autoplay === 'true';
      const speed = Number(slider.dataset.autoplaySpeed || 5000);
      let timer = null;
      if (!rail) return;

      const getStep = () => {
        const firstCard = rail.querySelector('.rosemary-card, .product-card, .rosemary-category-card');
        const gap = Number.parseFloat(getComputedStyle(rail).columnGap || '0');
        return firstCard ? firstCard.getBoundingClientRect().width + gap : rail.clientWidth * 0.85;
      };

      const move = (direction) => {
        const isRtl = document.documentElement.dir === 'rtl' || document.body.classList.contains('is-rtl');
        const multiplier = isRtl ? -1 : 1;
        rail.scrollBy({ left: direction * multiplier * getStep(), behavior: 'smooth' });
      };

      const canScrollForward = () => {
        const isRtl = document.documentElement.dir === 'rtl' || document.body.classList.contains('is-rtl');
        const maxScroll = rail.scrollWidth - rail.clientWidth - 4;
        return isRtl ? Math.abs(rail.scrollLeft) < maxScroll : rail.scrollLeft < maxScroll;
      };

      const resetToStart = () => {
        rail.scrollTo({ left: 0, behavior: 'smooth' });
      };

      const autoplayStep = () => {
        if (canScrollForward()) {
          move(1);
        } else {
          resetToStart();
        }
      };

      const stop = () => {
        if (timer) window.clearInterval(timer);
        timer = null;
      };

      const start = () => {
        stop();
        if (!autoplay || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        timer = window.setInterval(autoplayStep, Math.max(speed, 3000));
      };

      prev?.addEventListener('click', () => {
        stop();
        move(-1);
        start();
      });

      next?.addEventListener('click', () => {
        stop();
        move(1);
        start();
      });

      slider.addEventListener('mouseenter', stop);
      slider.addEventListener('mouseleave', start);
      slider.addEventListener('focusin', stop);
      slider.addEventListener('focusout', start);
      slider.addEventListener('touchstart', stop, { passive: true });
      slider.addEventListener('touchend', start, { passive: true });

      start();
    });
  };

  const initTrustSliders = () => {
    const mobileQuery = window.matchMedia('(max-width: 760px)');

    document.querySelectorAll(selectors.trustSlider).forEach((slider) => {
      if (slider.dataset.trustSliderReady === 'true') return;
      slider.dataset.trustSliderReady = 'true';

      const rail = slider.querySelector('[data-trust-rail]');
      const autoplay = slider.dataset.autoplay === 'true';
      const mobileSlider = slider.dataset.mobileSlider === 'true';
      const pauseOnHover = slider.dataset.pauseOnHover === 'true';
      const infiniteLoop = slider.dataset.infiniteLoop === 'true';
      const speed = Number(slider.dataset.autoplaySpeed || 4000);
      let timer = null;

      if (!rail) return;

      const isEnabled = () => (
        mobileQuery.matches &&
        mobileSlider &&
        slider.classList.contains('trust-mobile-horizontal_slider') &&
        rail.scrollWidth > rail.clientWidth + 2
      );

      const getStep = () => {
        const firstItem = rail.querySelector('.rosemary-trust__item');
        const gap = Number.parseFloat(getComputedStyle(rail).columnGap || getComputedStyle(rail).gap || '0');
        return firstItem ? firstItem.getBoundingClientRect().width + gap : rail.clientWidth * 0.7;
      };

      const isRtl = () => document.documentElement.dir === 'rtl' || document.body.classList.contains('is-rtl');

      const canScrollForward = () => {
        const maxScroll = rail.scrollWidth - rail.clientWidth - 4;
        return isRtl() ? Math.abs(rail.scrollLeft) < maxScroll : rail.scrollLeft < maxScroll;
      };

      const move = () => {
        if (!isEnabled()) return;
        if (canScrollForward()) {
          rail.scrollBy({ left: getStep() * (isRtl() ? -1 : 1), behavior: 'smooth' });
          return;
        }
        if (infiniteLoop) {
          rail.scrollTo({ left: 0, behavior: 'smooth' });
        }
      };

      const stop = () => {
        if (timer) window.clearInterval(timer);
        timer = null;
      };

      const start = () => {
        stop();
        if (!isEnabled() || !autoplay || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        timer = window.setInterval(move, Math.max(speed, 2000));
      };

      const refresh = () => {
        if (!isEnabled()) {
          stop();
          rail.scrollTo({ left: 0, behavior: 'auto' });
          return;
        }
        start();
      };

      if (pauseOnHover) {
        slider.addEventListener('mouseenter', stop);
        slider.addEventListener('mouseleave', start);
        slider.addEventListener('focusin', stop);
        slider.addEventListener('focusout', start);
        slider.addEventListener('touchstart', stop, { passive: true });
        slider.addEventListener('touchend', start, { passive: true });
        slider.addEventListener('touchcancel', start, { passive: true });
      }

      mobileQuery.addEventListener?.('change', refresh);
      window.addEventListener('resize', refresh, { passive: true });
      refresh();
    });
  };

  const initBeforeAfterComparisons = () => {
    document.querySelectorAll(selectors.beforeAfter).forEach((comparison) => {
      if (comparison.dataset.beforeAfterReady === 'true') return;
      comparison.dataset.beforeAfterReady = 'true';

      const media = comparison.querySelector('.rosemary-compare__media');
      const handle = comparison.querySelector('[data-before-after-handle]');
      if (!media || !handle) return;

      let dragging = false;

      const setPosition = (percent) => {
        const clamped = Math.min(96, Math.max(4, percent));
        media.style.setProperty('--compare-position', `${clamped}%`);
        media.style.setProperty('--compare-ratio', String(clamped / 100));
        handle.setAttribute('aria-valuenow', String(Math.round(clamped)));
      };

      const updateFromPointer = (event) => {
        const pointer = event.touches?.[0] || event;
        const rect = media.getBoundingClientRect();
        if (!rect.width) return;

        const isRtl = document.documentElement.dir === 'rtl' || document.body.classList.contains('is-rtl');
        const offset = isRtl ? rect.right - pointer.clientX : pointer.clientX - rect.left;
        setPosition((offset / rect.width) * 100);
      };

      const startDrag = (event) => {
        dragging = true;
        media.classList.add('is-dragging');
        updateFromPointer(event);
        media.setPointerCapture?.(event.pointerId);
      };

      const moveDrag = (event) => {
        if (!dragging) return;
        event.preventDefault();
        updateFromPointer(event);
      };

      const stopDrag = () => {
        dragging = false;
        media.classList.remove('is-dragging');
      };

      media.addEventListener('pointerdown', startDrag);
      media.addEventListener('pointermove', moveDrag);
      media.addEventListener('pointerup', stopDrag);
      media.addEventListener('pointercancel', stopDrag);
      media.addEventListener('pointerleave', stopDrag);

      handle.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();

        const current = Number.parseFloat(handle.getAttribute('aria-valuenow') || '50');
        const isRtl = document.documentElement.dir === 'rtl' || document.body.classList.contains('is-rtl');
        const step = event.shiftKey ? 10 : 4;

        if (event.key === 'Home') {
          setPosition(4);
        } else if (event.key === 'End') {
          setPosition(96);
        } else if (event.key === 'ArrowRight') {
          setPosition(current + (isRtl ? -step : step));
        } else if (event.key === 'ArrowLeft') {
          setPosition(current + (isRtl ? step : -step));
        }
      });

      setPosition(50);
    });
  };

  const initAnimations = () => {
    if (!window.theme?.settings?.enableRevealAnimations) return;
    const elements = [...document.querySelectorAll(selectors.animate)]
      .filter((element) => element.dataset.animateReady !== 'true');
    if (!elements.length) return;

    document.documentElement.classList.add('reveal-enabled');

    if (!('IntersectionObserver' in window)) {
      elements.forEach((element) => {
        element.dataset.animateReady = 'true';
        element.classList.add('is-visible');
      });
      return;
    }

    elements.forEach((element, index) => {
      element.dataset.animateReady = 'true';
      element.style.setProperty('--reveal-delay', `${Math.min(index * 35, 210)}ms`);
    });

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.16 });

    elements.forEach((element) => observer.observe(element));
  };

  const initPageTransitions = () => {
    if (!window.theme?.settings?.enablePageTransitions) return;

    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link) return;
      if (link.target || link.hasAttribute('download') || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin || url.hash && url.pathname === window.location.pathname) return;

      document.body.classList.add('is-loading');
    });

    window.addEventListener('pageshow', () => {
      document.body.classList.remove('is-loading');
    });
  };

  const initShopifySectionEvents = () => {
    document.addEventListener('shopify:section:load', () => {
      initHeroSliders();
      initProductSliders();
      initTrustSliders();
      initBeforeAfterComparisons();
      initAnimations();
      initStickyHeader();
    });
  };

  const initCommerceControls = () => {
    document.addEventListener('submit', async (event) => {
      const discountForm = event.target.closest('[data-cart-discount-form]');
      if (discountForm) {
        event.preventDefault();
        const input = discountForm.querySelector('input[name="discount"]');
        const submit = discountForm.querySelector('[type="submit"]');
        const code = input?.value.trim();
        if (!code || submit?.disabled) return;

        submit.disabled = true;
        submit.setAttribute('aria-busy', 'true');

        try {
          sessionStorage.setItem('rosemaryPendingDiscount', code);
          const discountUrl = new URL(discountForm.dataset.discountBase, window.location.origin);
          const localizedRoot = discountUrl.pathname.endsWith('/') ? discountUrl.pathname : `${discountUrl.pathname}/`;
          discountUrl.pathname = `${localizedRoot}discount/${encodeURIComponent(code)}`;
          discountUrl.searchParams.set('redirect', discountForm.dataset.cartUrl || window.theme.routes.cart);
          window.location.assign(discountUrl.toString());
        } catch (error) {
          const section = discountForm.closest('[data-main-cart-section]');
          const status = section?.querySelector('[data-cart-discount-status]');
          if (status) {
            status.textContent = section.dataset.discountError || error.message;
            status.classList.add('is-error');
          }
          submit.disabled = false;
          submit.removeAttribute('aria-busy');
        }
        return;
      }

      const form = event.target.closest('form[action*="/cart/add"]');
      if (!form || !window.theme?.settings?.enableCartDrawer) return;
      if (event.submitter?.matches('[data-buy-now]') || form.dataset.buyNow === 'true') return;
      event.preventDefault();

      const submit = form.querySelector('[type="submit"]');
      submit?.setAttribute('aria-busy', 'true');
      submit?.setAttribute('disabled', 'disabled');

      try {
        const addResponse = await fetch(`${window.theme.routes.cartAdd}.js`, {
          method: 'POST',
          body: new FormData(form),
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        if (!addResponse.ok) throw new Error('Cart add failed');
        await addResponse.json();

        const response = await fetch(`${window.theme.routes.cart}?sections=cart-drawer`);
        const sections = await response.json();
        const wrapper = document.getElementById('shopify-section-cart-drawer');
        if (wrapper && sections['cart-drawer']) {
          wrapper.innerHTML = sections['cart-drawer'];
        }

        document.querySelector(selectors.cartDrawer)?.open(submit || document.activeElement);
      } catch (error) {
        form.submit();
      } finally {
        submit?.removeAttribute('aria-busy');
        submit?.removeAttribute('disabled');
      }
    });

    document.addEventListener('click', async (event) => {
      const cartQuantityLink = event.target.closest('[data-cart-quantity-link]');
      const minus = event.target.closest('[data-quantity-minus]');
      const plus = event.target.closest('[data-quantity-plus]');
      const share = event.target.closest('[data-share-button]');

      if (cartQuantityLink) {
        event.preventDefault();
        const quantityControl = cartQuantityLink.closest('.quantity-control');
        const cartItem = cartQuantityLink.closest('[data-cart-item]');
        const mainCart = document.querySelector('[data-main-cart-section]');
        const status = mainCart?.querySelector('[data-cart-status]');
        let line = Number(cartQuantityLink.dataset.line);
        let quantity = Number(cartQuantityLink.dataset.quantity);

        if ((!line || Number.isNaN(quantity)) && cartQuantityLink.href) {
          const url = new URL(cartQuantityLink.href, window.location.origin);
          line = Number(url.searchParams.get('line'));
          quantity = Number(url.searchParams.get('quantity'));
        }

        if (!line || Number.isNaN(quantity)) {
          if (status) status.textContent = mainCart?.dataset.updateError || '';
          return;
        }

        if (cartItem?.getAttribute('aria-busy') === 'true') return;
        if (status) status.textContent = '';
        quantityControl?.setAttribute('aria-busy', 'true');
        cartItem?.setAttribute('aria-busy', 'true');
        cartItem?.querySelectorAll('button').forEach((button) => { button.disabled = true; });

        try {
          const mainCartWrapper = mainCart?.closest('.shopify-section');
          const sections = [];
          const mainCartSectionId = mainCartWrapper?.id?.replace('shopify-section-', '');

          if (window.theme?.settings?.enableCartDrawer) {
            sections.push('cart-drawer');
          }

          if (mainCartSectionId) {
            sections.push(mainCartSectionId);
          }

          const response = await fetch(`${window.theme.routes.cartChange}.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              line,
              quantity,
              sections,
              sections_url: window.location.pathname
            })
          });

          if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            throw new Error(errorPayload.description || mainCart?.dataset.updateError || 'Cart quantity update failed');
          }

          const cart = await response.json();
          const wrapper = document.getElementById('shopify-section-cart-drawer');
          const drawerWasOpen = wrapper?.querySelector(selectors.cartDrawer)?.getAttribute('aria-hidden') === 'false';

          if (wrapper && cart.sections?.['cart-drawer']) {
            wrapper.innerHTML = cart.sections['cart-drawer'];
            if (drawerWasOpen) {
              wrapper.querySelector(selectors.cartDrawer)?.open(cartQuantityLink);
            }
          }

          if (mainCartWrapper && mainCartSectionId && cart.sections?.[mainCartSectionId]) {
            mainCartWrapper.innerHTML = cart.sections[mainCartSectionId];
          } else if (mainCart) {
            throw new Error(mainCart.dataset.updateError || 'Cart section update failed');
          }

          document.querySelectorAll('[data-cart-count]').forEach((node) => {
            node.textContent = String(cart.item_count);
          });

          const updatedMainCart = document.querySelector('[data-main-cart-section]');
          const updatedStatus = updatedMainCart?.querySelector('[data-cart-status]');
          if (updatedStatus) updatedStatus.textContent = updatedMainCart.dataset.updateSuccess || '';
        } catch (error) {
          if (status) status.textContent = error.message || mainCart?.dataset.updateError || '';
        } finally {
          quantityControl?.removeAttribute('aria-busy');
          if (cartItem?.isConnected) {
            cartItem.removeAttribute('aria-busy');
            cartItem.querySelectorAll('button').forEach((button) => { button.disabled = false; });
          }
        }

        return;
      }

      if (minus || plus) {
        const input = event.target.closest('.quantity-control')?.querySelector('input[type="number"]');
        if (!input) return;
        const current = Number(input.value || 1);
        const min = Number(input.min || 1);
        input.value = String(Math.max(min, current + (plus ? 1 : -1)));
      }

      if (share) {
        if (navigator.share) {
          await navigator.share({ title: document.title, url: window.location.href });
        } else {
          await navigator.clipboard?.writeText(window.location.href);
          share.classList.add('is-copied');
          window.setTimeout(() => share.classList.remove('is-copied'), 1200);
        }
      }
    });

    const cartSection = document.querySelector('[data-main-cart-section]');
    const discountStatus = cartSection?.querySelector('[data-cart-discount-status]');
    let pendingDiscount = '';
    try {
      pendingDiscount = sessionStorage.getItem('rosemaryPendingDiscount') || '';
    } catch (error) {
      pendingDiscount = '';
    }

    if (cartSection && discountStatus && pendingDiscount) {
      fetch(`${window.theme.routes.cart}.js`, { headers: { Accept: 'application/json' } })
        .then((response) => {
          if (!response.ok) throw new Error('Discount verification failed');
          return response.json();
        })
        .then((cart) => {
          const applications = [
            ...(cart.cart_level_discount_applications || []),
            ...(cart.discount_applications || [])
          ];
          const normalizedCode = pendingDiscount.toLocaleLowerCase();
          const applied = applications.some((application) =>
            String(application.title || '').toLocaleLowerCase().includes(normalizedCode)
          );
          discountStatus.textContent = applied ? cartSection.dataset.discountSuccess : cartSection.dataset.discountError;
          discountStatus.classList.toggle('is-error', !applied);
        })
        .catch(() => {
          discountStatus.textContent = cartSection.dataset.discountError || '';
          discountStatus.classList.add('is-error');
        })
        .finally(() => {
          try {
            sessionStorage.removeItem('rosemaryPendingDiscount');
          } catch (error) {
            return;
          }
        });
    }
  };

  const initTouchFeedback = () => {
    document.addEventListener('pointerdown', (event) => {
      const target = event.target.closest('.button, .icon-button, .rosemary-card, .rosemary-mobile-nav a');
      target?.classList.add('is-pressed');
    }, { passive: true });

    document.addEventListener('pointerup', (event) => {
      event.target.closest('.is-pressed')?.classList.remove('is-pressed');
    }, { passive: true });

    document.addEventListener('pointercancel', () => {
      document.querySelectorAll('.is-pressed').forEach((element) => element.classList.remove('is-pressed'));
    }, { passive: true });
  };

  const initFloatingAppOffsets = () => {
    const mobileQuery = window.matchMedia('(max-width: 760px)');
    const rewardSelectors = [
      '.smile-launcher-frame-container',
      '.smile-launcher-frame',
      '#smile-ui-lite-launcher-frame-container',
      'iframe[src*="smile"]',
      'iframe[src*="sweettooth"]',
      'iframe[src*="loyalty"]',
      'iframe[title*="Rewards" i]',
      'iframe[title*="reward" i]',
      '[class*="smile-launcher"]',
      '[id*="smile-launcher"]',
      '[class*="rewards-launcher"]',
      '[id*="rewards-launcher"]'
    ];

    const isLikelyBottomLauncher = (element) => {
      if (element.matches('.rosemary-mobile-nav, .rosemary-mobile-nav *, .rosemary-whatsapp-float')) return false;
      const style = window.getComputedStyle(element);
      if (style.position !== 'fixed') return false;
      const rect = element.getBoundingClientRect();
      return rect.width >= 48 && rect.height >= 36 && rect.bottom > window.innerHeight - 140;
    };

    const getLaunchers = () => {
      const matched = rewardSelectors.flatMap((selector) => [...document.querySelectorAll(selector)]);
      const fixedBottom = [...document.querySelectorAll('iframe, [role="button"], button, a')].filter(isLikelyBottomLauncher);
      return [...new Set([...matched, ...fixedBottom])];
    };

    const apply = () => {
      getLaunchers().forEach((element) => {
        if (!mobileQuery.matches) {
          element.style.removeProperty('bottom');
          element.style.removeProperty('right');
          element.style.removeProperty('left');
          element.style.removeProperty('transform');
          element.style.removeProperty('transform-origin');
          return;
        }

        element.style.setProperty('bottom', 'calc(9.25rem + env(safe-area-inset-bottom))', 'important');
        element.style.setProperty('right', '.55rem', 'important');
        element.style.setProperty('left', 'auto', 'important');
        element.style.setProperty('transform', 'scale(.72)', 'important');
        element.style.setProperty('transform-origin', 'bottom right', 'important');
      });
    };

    apply();
    window.setTimeout(apply, 600);
    window.setTimeout(apply, 1600);
    window.setTimeout(apply, 3600);
    window.addEventListener('resize', apply);
    mobileQuery.addEventListener?.('change', apply);

    const observer = new MutationObserver(() => window.requestAnimationFrame(apply));
    observer.observe(document.body, { childList: true, subtree: true });
  };



  /* Rosemary draft product page behavior - transferred safely. */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(this.querySelector('[data-product-variants]')?.textContent || '[]');
      this.form = this.querySelector('[data-product-form]');
      this.variantInput = this.querySelector('[data-variant-id-input]');
      this.priceContainer = this.querySelector('[data-product-price]');
      this.inventoryStatus = this.querySelector('[data-inventory-status]');
      this.addButton = this.querySelector('[data-add-to-cart]');
      this.addButtonText = this.querySelector('[data-add-to-cart-text]');
      this.saleBadge = this.querySelector('[data-sale-badge]');
      this.mobileBar = this.querySelector('[data-mobile-atc-bar]');
      this.mobileTrigger = this.querySelector('[data-mobile-atc-trigger]');
      this.mobilePrice = this.querySelector('[data-mobile-atc-price]');
      this.whatsappLink = this.querySelector('[data-whatsapp-link]');

      this.bindVariantPicker();
      this.bindGallery();
      this.bindQuantity();
      this.bindProductFormSubmit();
      this.bindMobileBar();
      this.storeRecentlyViewed();
      this.renderRecentlyViewed();
    }

    bindVariantPicker() {
      this.querySelectorAll('[data-option-position]').forEach((input) => {
        input.addEventListener('change', () => this.onVariantChange());
      });
    }

    bindGallery() {
      this.querySelectorAll('[data-product-thumbnail]').forEach((button) => {
        button.addEventListener('click', () => this.setActiveMedia(button.dataset.mediaId));
      });
    }

    bindQuantity() {
      const input = this.querySelector('[data-quantity-input]');
      this.querySelector('[data-quantity-minus]')?.addEventListener('click', () => {
        if (!input) return;
        input.value = Math.max(1, Number(input.value || 1) - 1);
      });
      this.querySelector('[data-quantity-plus]')?.addEventListener('click', () => {
        if (!input) return;
        input.value = Number(input.value || 1) + 1;
      });
    }

    bindProductFormSubmit() {
      if (!this.form) return;

      this.form.addEventListener('submit', (event) => {
        const submitter = event.submitter;
        if (submitter && submitter.name !== 'add') return;

        event.preventDefault();
        this.addToCart();
      });
    }

    addToCart() {
      if (!this.form || !this.addButton) return;

      const originalText = this.addButtonText?.textContent || '';
      this.addButton.disabled = true;
      this.addButton.classList.add('is-loading');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: new FormData(this.form)
      })
        .then((response) => {
          if (!response.ok) throw new Error('Cart add failed');
          return fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
        })
        .then((response) => response.json())
        .then((cart) => {
          document.querySelectorAll('cart-form').forEach((cartForm) => cartForm.updateCart(cart));
          this.animateAddToCart();
        })
        .catch(() => {
          this.form.submit();
        })
        .finally(() => {
          this.addButton.disabled = false;
          this.addButton.classList.remove('is-loading');
          if (this.addButtonText) this.addButtonText.textContent = originalText;
        });
    }

    animateAddToCart() {
      const cartButton = document.querySelector('[data-cart-button]');
      const image = this.querySelector('.product-gallery__media.is-active img') || this.querySelector('.product-gallery__media img');

      cartButton?.classList.add('cart-button--pulse');
      window.setTimeout(() => cartButton?.classList.remove('cart-button--pulse'), 650);

      if (!cartButton || !image || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const imageRect = image.getBoundingClientRect();
      const cartRect = cartButton.getBoundingClientRect();
      const flyer = image.cloneNode();
      const size = Math.min(92, Math.max(54, imageRect.width * 0.16));

      flyer.className = 'add-to-cart-flyer';
      flyer.style.width = `${size}px`;
      flyer.style.height = `${size}px`;
      flyer.style.insetInlineStart = `${imageRect.left + imageRect.width / 2 - size / 2}px`;
      flyer.style.insetBlockStart = `${imageRect.top + imageRect.height / 2 - size / 2}px`;
      flyer.style.setProperty('--fly-x', `${cartRect.left + cartRect.width / 2 - imageRect.left - imageRect.width / 2}px`);
      flyer.style.setProperty('--fly-y', `${cartRect.top + cartRect.height / 2 - imageRect.top - imageRect.height / 2}px`);

      document.body.appendChild(flyer);
      flyer.addEventListener('animationend', () => flyer.remove(), { once: true });
    }

    bindMobileBar() {
      if (!this.mobileBar || !this.mobileTrigger || !this.addButton) return;
      this.mobileTrigger.addEventListener('click', () => this.addButton.click());

      const observer = new IntersectionObserver(([entry]) => {
        this.mobileBar.classList.toggle('is-visible', !entry.isIntersecting);
      }, { threshold: 0.1 });

      observer.observe(this.addButton);
    }

    onVariantChange() {
      const selectedOptions = Array.from(this.querySelectorAll('[data-option-position]:checked'))
        .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
        .map((input) => input.value);

      const variant = this.variants.find((item) => {
        return item.options.every((option, index) => option === selectedOptions[index]);
      });

      if (!variant) return;

      this.updateVariantInput(variant);
      this.updatePrice(variant);
      this.updateAvailability(variant);
      this.updateMedia(variant);
      this.updateUrl(variant);
      this.updateWhatsapp(variant);
    }

    updateVariantInput(variant) {
      if (this.variantInput) this.variantInput.value = variant.id;
    }

    updatePrice(variant) {
      const price = this.formatMoney(variant.price);
      const compare = variant.compare_at_price ? this.formatMoney(variant.compare_at_price) : '';
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;

      if (this.priceContainer) {
        const regular = this.priceContainer.querySelector('.price__regular');
        const sale = this.priceContainer.querySelector('.price__sale');
        const compareEl = this.priceContainer.querySelector('.price__compare');
        const priceEl = this.priceContainer.querySelector('.price');

        if (regular) regular.textContent = price;
        if (sale) sale.textContent = price;
        if (compareEl) compareEl.textContent = compare;
        if (priceEl) priceEl.classList.toggle('price--on-sale', Boolean(onSale));
      }

      if (this.mobilePrice) this.mobilePrice.textContent = price;
      if (this.saleBadge) this.saleBadge.classList.toggle('is-hidden', !onSale);
    }

    updateAvailability(variant) {
      const available = Boolean(variant.available);
      const addLabel = this.dataset.addLabel || '';
      const soldOutLabel = this.dataset.soldOutLabel || '';

      if (this.addButton) this.addButton.disabled = !available;
      if (this.mobileTrigger) this.mobileTrigger.disabled = !available;
      if (this.addButtonText) this.addButtonText.textContent = available ? addLabel : soldOutLabel;
      if (this.mobileTrigger) this.mobileTrigger.textContent = available ? addLabel : soldOutLabel;

      if (this.inventoryStatus) {
        this.inventoryStatus.textContent = available ? this.dataset.inStockLabel : this.dataset.outOfStockLabel;
        this.inventoryStatus.classList.toggle('inventory-status--out', !available);
      }
    }

    updateMedia(variant) {
      const mediaId = variant.featured_media?.id;
      if (mediaId) this.setActiveMedia(String(mediaId));
    }

    setActiveMedia(mediaId) {
      this.querySelectorAll('[data-product-media]').forEach((media) => {
        const active = media.dataset.mediaId === mediaId;
        media.classList.toggle('is-active', active);
        media.hidden = !active;
      });

      this.querySelectorAll('[data-product-thumbnail]').forEach((thumbnail) => {
        const active = thumbnail.dataset.mediaId === mediaId;
        thumbnail.classList.toggle('is-active', active);
        thumbnail.setAttribute('aria-current', String(active));
      });
    }

    updateUrl(variant) {
      const url = `${this.dataset.productUrl}?variant=${variant.id}`;
      window.history.replaceState({ path: url }, '', url);
    }

    updateWhatsapp(variant) {
      if (!this.whatsappLink) return;
      const template = this.whatsappLink.dataset.whatsappTemplate || '';
      const message = template
        .replaceAll('[product_title]', this.whatsappLink.dataset.productTitle || '')
        .replaceAll('[product_url]', this.whatsappLink.dataset.productUrl || '')
        .replaceAll('[variant_title]', variant.title || '');

      this.whatsappLink.href = `https://wa.me/${this.whatsappLink.dataset.whatsappPhone}?text=${encodeURIComponent(message)}`;
    }

    formatMoney(cents) {
      const amount = (Number(cents || 0) / 100).toFixed(2);
      const format = this.dataset.moneyFormat || '${{amount}}';
      return format
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(Number(amount))))
        .replace(/\{\{\s*amount\s*\}\}/g, amount)
        .replace(/<[^>]*>/g, '');
    }

    storeRecentlyViewed() {
      const script = this.querySelector('[data-current-product-card]');
      if (!script) return;

      try {
        const product = JSON.parse(script.textContent);
        const items = JSON.parse(localStorage.getItem('recently-viewed-products') || '[]')
          .filter((item) => item.handle !== product.handle);
        items.unshift(product);
        localStorage.setItem('recently-viewed-products', JSON.stringify(items.slice(0, 8)));
      } catch (error) {
        localStorage.removeItem('recently-viewed-products');
      }
    }

    renderRecentlyViewed() {
      const section = this.querySelector('[data-recently-viewed-section]');
      const list = this.querySelector('[data-recently-viewed-list]');
      const current = this.querySelector('[data-current-product-card]');
      if (!section || !list || !current) return;

      const currentProduct = JSON.parse(current.textContent);
      const items = JSON.parse(localStorage.getItem('recently-viewed-products') || '[]')
        .filter((item) => item.handle !== currentProduct.handle)
        .slice(0, 4);

      section.classList.toggle('is-empty', items.length === 0);
      list.innerHTML = items.map((item) => `
        <a class="recently-viewed-card product-carousel__item" href="${this.escapeHtml(item.url)}">
          ${item.image ? `<img src="${this.escapeHtml(item.image)}" alt="${this.escapeHtml(item.title)}" loading="lazy">` : ''}
          <span class="recently-viewed-card__info">
            <span class="recently-viewed-card__title">${this.escapeHtml(item.title)}</span>
            <span class="recently-viewed-card__price">${this.escapeHtml(item.price)}</span>
          </span>
        </a>
      `).join('');
    }

    escapeHtml(value) {
      return String(value || '').replace(/[&<>"']/g, (character) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      })[character]);
    }
  }

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url) return;

      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((text) => {
          const html = new DOMParser().parseFromString(text, 'text/html');
          const recommendations = html.querySelector('product-recommendations');
          if (recommendations?.innerHTML.trim().length) this.innerHTML = recommendations.innerHTML;
        })
        .catch(() => {});
    }
  }

  if (!customElements.get('product-info')) {
    customElements.define('product-info', ProductInfo);
  }

  if (!customElements.get('product-recommendations')) {
    customElements.define('product-recommendations', ProductRecommendations);
  }


  const registerElements = () => {
    if (!customElements.get('cart-drawer')) {
      customElements.define('cart-drawer', CartDrawer);
    }

    if (!customElements.get('rosemary-disclosure')) {
      customElements.define('rosemary-disclosure', Disclosure);
    }
  };

  const init = () => {
    registerElements();
    initStickyHeader();
    initLanguageSwitcher();
    initHeroSliders();
    initProductSliders();
    initTrustSliders();
    initBeforeAfterComparisons();
    initAnimations();
    initPageTransitions();
    initShopifySectionEvents();
    initCommerceControls();
    initTouchFeedback();
    initFloatingAppOffsets();
    document.dispatchEvent(new CustomEvent('rosemary:ready'));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
